import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { common_utils } from "../../../../common/common_utils";
import { TimeFinderOption } from "../../../../src/api/trpc/time-finder";
import { SeshPrimaryButton } from "../../dashboard/forms/improved_components/base_components/sesh_primary_button";
import { TextAreaForm } from "../../dashboard/forms/text_area";
import { trpc } from "../../dashboard/utils/trpc";
import { updateSelectedDatesOnSpecifyTimeToggled } from "../utils";
import { CalendarPicker } from "./calendar-picker";
import { InputSection } from "./input-section";
import { Constants } from "../../../../common/constants";
import { DashboardUtils } from "../../dashboard/dashboard_utils";
import { SettingsBaseItem } from "../../dashboard/forms/improved_components/settings_items/settings_base_item";
import Switch from "react-switch";
import ChannelPicker from "../../dashboard/forms/improved_components/channel_picker";
import { EligibleRolesSettingsItem } from "./eligible_roles_settings_item";
import { CombinedMentionsDropdown } from "./combined_mentions_dropdown";
import { Mentionable } from "../../../../common/sesh_types";

// Settings descriptions and titles for consistent use across components
export const TIME_FINDER_SETTINGS = {
	anonymous_voting: {
		title: "Anonymous Voting",
		description:
			"Only poll creator and server admins can see the names of voters",
	},
	hide_votes: {
		title: "Hide Results Until Voted",
		description:
			"Non-admins can not see other people's votes until after they've voted",
	},
	send_to_discord: {
		title: "Send Time Finder to Discord",
		description:
			"Post this Time Finder poll to a Discord channel, allowing users to select their availability without leaving Discord",
	},
};

type AppTimeFinderBaseProps = {
	onSubmit: (data: {
		title: string;
		description: string;
		selected_dates: TimeFinderOption[];
		are_times_specified: boolean;
		is_public: boolean;
		anonymous_voting: boolean;
		hide_votes: boolean;
		channel_id?: string;
		eligible_role_ids: string[];
		user_mentions_on_create: string[];
		role_mentions_on_create: string[];
	}) => void;
	is_loading: boolean;
	is_public: boolean;
	is_on_dashboard: boolean;
	does_have_premium: boolean;
	roles?: Mentionable[];
};

type AppTimeFinderCreateProps = AppTimeFinderBaseProps & {
	is_edit?: false;
};

type AppTimeFinderEditProps = AppTimeFinderBaseProps & {
	is_edit: true;
	initial_data: {
		title: string;
		description: string;
		selected_dates: TimeFinderOption[];
		are_times_specified: boolean;
		anonymous_voting: boolean;
		hide_votes: boolean;
		eligible_role_ids?: string[];
		user_mentions_on_create?: string[];
		role_mentions_on_create?: string[];
		channel_id?: string;
		channel_name?: string;
	};
};

export type AppTimeFinderProps =
	| AppTimeFinderCreateProps
	| AppTimeFinderEditProps;

export function AppTimeFinder(props: AppTimeFinderProps) {
	const [title, setTitle] = useState(
		props.is_edit ? props.initial_data.title : ""
	);
	const [description, setDescription] = useState(
		props.is_edit ? props.initial_data.description : ""
	);
	const [selectedDates, setSelectedDates] = useState<TimeFinderOption[]>(
		props.is_edit ? props.initial_data.selected_dates : []
	);
	const [areTimesSpecified, setAreTimesSpecified] = useState(
		props.is_edit ? props.initial_data.are_times_specified : false
	);
	const [isPublic, setIsPublic] = useState(props.is_public);
	const [anonymousVoting, setAnonymousVoting] = useState(
		props.is_edit ? props.initial_data.anonymous_voting : false
	);
	const [hideVotes, setHideVotes] = useState(
		props.is_edit ? props.initial_data.hide_votes : false
	);
	const [channelId, setChannelId] = useState<string | undefined>(undefined);
	const [sendToDiscord, setSendToDiscord] = useState(false);
	const [isRestrictRolesEnabled, setIsRestrictRolesEnabled] = useState(
		props.is_edit && props.initial_data.eligible_role_ids
			? props.initial_data.eligible_role_ids.length > 0
			: false
	);
	const [eligibleRoleIds, setEligibleRoleIds] = useState<string[]>(
		props.is_edit && props.initial_data.eligible_role_ids
			? props.initial_data.eligible_role_ids
			: []
	);
	const [userMentionsOnCreate, setUserMentionsOnCreate] = useState<string[]>(
		props.is_edit && props.initial_data.user_mentions_on_create
			? props.initial_data.user_mentions_on_create
			: []
	);
	const [roleMentionsOnCreate, setRoleMentionsOnCreate] = useState<
		Mentionable[]
	>(
		props.is_edit &&
			props.initial_data.role_mentions_on_create &&
			props.roles
			? props.roles.filter((role) =>
					props.initial_data.role_mentions_on_create!.includes(
						role.id
					)
			  )
			: []
	);

	function validateForm(): string[] {
		const errors: string[] = [];

		if (title.trim().length === 0) {
			errors.push("Title is required");
		}

		if (title.length > Constants.EVENT_CHAR_LIMIT) {
			errors.push(
				`Title must be less than ${Constants.EVENT_CHAR_LIMIT} characters`
			);
		}

		if (description.length > Constants.TIME_FINDER_DESCRIPTION_CHAR_LIMIT) {
			errors.push(
				`Description must be less than ${Constants.TIME_FINDER_DESCRIPTION_CHAR_LIMIT} characters`
			);
		}

		if (selectedDates.length === 0) {
			errors.push("Select at least one date");
		}

		if (sendToDiscord && !channelId) {
			errors.push("Select a channel to send the Time Finder to");
		}

		return errors;
	}

	function handleSubmit() {
		const errors = validateForm();

		if (errors.length > 0) {
			// Show errors in modal with improved styling
			DashboardUtils.ShowDefaultErrorAlert(
				<div className="text-left">
					<p className="mb-4 text-lg font-semibold">
						Please address the following:
					</p>
					<div className="rounded-md border-l-4 border-red-500 bg-red-500/10 p-4">
						<ul className="space-y-2">
							{errors.map((error, index) => (
								<li key={index} className="flex items-start">
									<span className="mr-2 text-red-500">
										<i
											className="fas fa-exclamation-circle"
											aria-hidden="true"
										></i>
									</span>
									<span>{error}</span>
								</li>
							))}
						</ul>
					</div>
				</div>,
				"Please Check Your Input"
			);
			return;
		}

		// If no errors, proceed with form submission
		props.onSubmit({
			title,
			description,
			selected_dates: selectedDates.map((val) =>
				areTimesSpecified ? val : { ...val, timeslots: undefined }
			),
			are_times_specified: areTimesSpecified,
			is_public: isPublic,
			anonymous_voting: anonymousVoting,
			hide_votes: hideVotes,
			channel_id: sendToDiscord ? channelId : undefined,
			eligible_role_ids: isRestrictRolesEnabled ? eligibleRoleIds : [],
			user_mentions_on_create: sendToDiscord ? userMentionsOnCreate : [],
			role_mentions_on_create: sendToDiscord
				? roleMentionsOnCreate.map((r) => r.id)
				: [],
		});
	}

	function onUpdateSelectedDates(newSelectedDates: TimeFinderOption[]) {
		newSelectedDates.sort((a, b) => {
			return (
				a.date.year - b.date.year ||
				a.date.month - b.date.month ||
				a.date.day - b.date.day
			);
		});

		// Remove duplicate dates by comparing day, month, and year
		const uniqueDates: TimeFinderOption[] = [];

		for (const date of newSelectedDates) {
			const isDuplicate = uniqueDates.some(
				(existingDate) =>
					existingDate.date.day === date.date.day &&
					existingDate.date.month === date.date.month &&
					existingDate.date.year === date.date.year
			);

			if (!isDuplicate) {
				// If the date has timeslots, ensure they are unique
				if (date.timeslots && date.timeslots.length > 0) {
					const uniqueTimeslots = [];

					for (const timeslot of date.timeslots) {
						const isTimeslotDuplicate = uniqueTimeslots.some(
							(existingTimeslot) =>
								existingTimeslot.start_time ===
									timeslot.start_time &&
								existingTimeslot.end_time === timeslot.end_time
						);

						if (!isTimeslotDuplicate) {
							uniqueTimeslots.push(timeslot);
						}
					}

					date.timeslots = uniqueTimeslots;
				}

				uniqueDates.push(date);
			}
		}

		setSelectedDates(uniqueDates);
	}

	return (
		<div className="">
			<div className="">
				<div className="flex flex-col gap-4">
					<InputSection title="Event Details">
						<div className="flex flex-col gap-4">
							<div>
								<div className="mb-2 flex items-center gap-2 pt-4">
									<label className="!mb-0 text-lg font-bold">
										Title
									</label>
								</div>
								<div className="control">
									<input
										className="input"
										type="text"
										placeholder="Enter event title"
										value={title}
										onChange={(e) =>
											setTitle(e.target.value)
										}
										maxLength={Constants.EVENT_CHAR_LIMIT}
									/>
								</div>
							</div>

							<div>
								<div className="mb-2 flex items-center gap-2">
									<label className="!mb-0 text-lg font-bold">
										Description
									</label>
								</div>
								<div className="control">
									<TextAreaForm
										placeholder="Enter event description (optional)"
										initial_value={description}
										updateFunc={(val) =>
											setDescription(val)
										}
										rows={4}
										char_limit={
											Constants.TIME_FINDER_DESCRIPTION_CHAR_LIMIT
										}
									/>
								</div>
							</div>
						</div>
					</InputSection>

					<InputSection title="Days & Times">
						<div className="flex w-full flex-col gap-4">
							<div className="calendar-container w-full">
								<CalendarPicker
									selectedDates={selectedDates}
									areTimesSpecified={areTimesSpecified}
									onUpdate={onUpdateSelectedDates}
									onSpecifyTimesChange={(specifyTimes) => {
										if (specifyTimes) {
											setSelectedDates(
												updateSelectedDatesOnSpecifyTimeToggled
											);
										}

										setAreTimesSpecified(specifyTimes);
									}}
								/>
							</div>
						</div>
					</InputSection>

					<InputSection
						title={
							<div className="flex flex-col gap-1">
								<div>Settings</div>
								{!props.is_on_dashboard && (
									<div className="text-base-soft text-xs italic">
										Advanced settings are only available in
										the{" "}
										<a
											href="/dashboard"
											className="manual-link"
										>
											sesh dashboard
										</a>
									</div>
								)}
							</div>
						}
					>
						<div className="pt-4">
							<SettingsBaseItem label="Visibility" no_border>
								{props.is_public ||
								props.is_edit ||
								!props.is_on_dashboard ? (
									<div className="flex flex-col gap-1">
										<select
											disabled
											className="w-full cursor-not-allowed rounded-md border-none bg-base-dark/50 px-3 py-2 text-sm font-medium text-white/70"
											value={
												props.is_public
													? "public"
													: "members"
											}
										>
											<option value="public">
												Anyone with the Link
											</option>
											{!props.is_public && (
												<option value="members">
													Server Members
												</option>
											)}
										</select>
										<div className="text-base-soft mt-1 text-xs italic">
											{props.is_edit && (
												<>
													Visibility cannot be changed
													after poll creation
												</>
											)}
										</div>
									</div>
								) : (
									<select
										className="w-full rounded-md border-none bg-base-dark px-3 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-sesh-primary"
										value={isPublic ? "public" : "members"}
										onChange={(e) => {
											const is_public =
												e.target.value === "public";

											setIsPublic(is_public);

											if (is_public) {
												setIsRestrictRolesEnabled(
													false
												);
											}
										}}
									>
										<option value="members">
											Server Members
										</option>
										<option value="public">
											Anyone with the Link
										</option>
									</select>
								)}
							</SettingsBaseItem>

							<div className="my-6 border-t border-white/10"></div>

							{(!props.is_edit || props.is_public) && (
								<SettingsBaseItem
									label={
										TIME_FINDER_SETTINGS.send_to_discord
											.title
									}
									description={
										TIME_FINDER_SETTINGS.send_to_discord
											.description
									}
									no_border
									below_element={
										sendToDiscord ? (
											<div className="space-y-4 border-t border-white/10 pt-4">
												{/* Channel Selection */}
												<div className="space-y-2">
													<div className="font-bold">
														Channel
													</div>
													<div className="mb-2 text-sm text-content-secondary">
														Select which channel to
														post the Time Finder to
													</div>
													<ChannelPicker
														channel_id={channelId}
														setChannel={(channel) =>
															setChannelId(
																channel
															)
														}
													/>
												</div>

												{/* Mentions Section */}
												<div className="space-y-2">
													<div className="font-bold">
														Mentions
													</div>
													<div className="mb-2 text-sm text-content-secondary">
														Choose users and roles
														to mention when posting
													</div>
													<CombinedMentionsDropdown
														roles={props.roles}
														initial_value={{
															user_mentions:
																userMentionsOnCreate,
															role_mentions:
																roleMentionsOnCreate,
														}}
														updateFunc={(val) => {
															setUserMentionsOnCreate(
																val.user_mentions
															);
															setRoleMentionsOnCreate(
																val.role_mentions
															);
														}}
														disabled={
															!props.is_on_dashboard
														}
													/>
												</div>
											</div>
										) : undefined
									}
								>
									<div className="flex items-center justify-end">
										<Switch
											checked={sendToDiscord}
											onChange={(checked) => {
												setSendToDiscord(checked);
												if (!checked) {
													setChannelId(undefined);
													setUserMentionsOnCreate([]);
													setRoleMentionsOnCreate([]);
												}
											}}
											disabled={!props.is_on_dashboard}
											onColor="#00c3cc"
											onHandleColor="#00f4ff"
											checkedIcon={
												<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
													<i className="fas fa-check"></i>
												</span>
											}
											uncheckedIcon={
												<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
													<i className="fas fa-times"></i>
												</span>
											}
										/>
									</div>
								</SettingsBaseItem>
							)}

							{/* Show Discord settings in disabled state when editing */}
							{props.is_edit && (
								<SettingsBaseItem
									label={
										TIME_FINDER_SETTINGS.send_to_discord
											.title
									}
									description="Discord settings cannot be changed after creation"
									no_border
									below_element={
										<div className="space-y-4 border-t border-white/10 pt-4 opacity-60">
											{/* Channel Section - Show current settings */}
											<div className="space-y-2">
												<div className="font-bold">
													Discord Channel
												</div>
												<div className="mb-2 text-sm text-content-secondary">
													{props.is_edit &&
													props.initial_data
														?.channel_id
														? "This Time Finder was sent to the following Discord channel:"
														: "This Time Finder was not sent to Discord when created"}
												</div>
												{props.is_edit &&
													props.initial_data
														?.channel_id && (
														<div className="w-full rounded-md border-none bg-base-dark/50 px-3 py-2 text-sm font-medium text-white/70">
															{props.initial_data
																.channel_name ||
																props
																	.initial_data
																	.channel_id}
														</div>
													)}
											</div>

											{/* Mentions Section - Show current settings */}
											<div className="space-y-2">
												<div className="font-bold">
													Current Mentions
												</div>
												<div className="mb-2 text-sm text-content-secondary">
													{props.initial_data
														?.user_mentions_on_create
														?.length > 0 ||
													props.initial_data
														?.role_mentions_on_create
														?.length > 0
														? `These mentions were set when the Time Finder was created:`
														: "No mentions were set when this Time Finder was created"}
												</div>
												{(props.initial_data
													?.user_mentions_on_create
													?.length > 0 ||
													props.initial_data
														?.role_mentions_on_create
														?.length > 0) && (
													<div className="space-y-2">
														{props.initial_data
															?.user_mentions_on_create
															?.length > 0 && (
															<div className="w-full rounded-md border-none bg-base-dark/50 px-3 py-2 text-sm font-medium text-white/70">
																<div className="mb-1 font-semibold">
																	Users (
																	{
																		props
																			.initial_data
																			.user_mentions_on_create
																			.length
																	}
																	):
																</div>
																<div className="text-xs text-white/60">
																	{props.initial_data.user_mentions_on_create.map(
																		(
																			userId,
																			index
																		) => (
																			<span
																				key={
																					userId
																				}
																			>
																				{
																					userId
																				}
																				{index <
																				props
																					.initial_data
																					.user_mentions_on_create
																					.length -
																					1
																					? ", "
																					: ""}
																			</span>
																		)
																	)}
																</div>
															</div>
														)}
														{props.initial_data
															?.role_mentions_on_create
															?.length > 0 && (
															<div className="w-full rounded-md border-none bg-base-dark/50 px-3 py-2 text-sm font-medium text-white/70">
																<div className="mb-1 font-semibold">
																	Roles (
																	{
																		props
																			.initial_data
																			.role_mentions_on_create
																			.length
																	}
																	):
																</div>
																<div className="text-xs text-white/60">
																	{props.initial_data.role_mentions_on_create.map(
																		(
																			roleId,
																			index
																		) => {
																			const role =
																				props.roles?.find(
																					(
																						r
																					) =>
																						r.id ===
																						roleId
																				);
																			return (
																				<span
																					key={
																						roleId
																					}
																					className="inline-flex items-center gap-1"
																				>
																					{role && (
																						<div
																							className="h-2 w-2 rounded-full"
																							style={{
																								backgroundColor:
																									role.color ||
																									"#99aab5",
																							}}
																						/>
																					)}
																					{role?.name ||
																						roleId}
																					{index <
																					props
																						.initial_data
																						.role_mentions_on_create
																						.length -
																						1
																						? ", "
																						: ""}
																				</span>
																			);
																		}
																	)}
																</div>
															</div>
														)}
													</div>
												)}
											</div>
										</div>
									}
								>
									<div className="flex items-center justify-end">
										<Switch
											checked={
												(props.is_edit &&
													!!props.initial_data
														?.channel_id) ||
												userMentionsOnCreate.length >
													0 ||
												roleMentionsOnCreate.length > 0
											}
											onChange={() => {
												// Do nothing - disabled
											}}
											disabled={true}
											onColor="#00c3cc"
											onHandleColor="#00f4ff"
											checkedIcon={
												<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
													<i className="fas fa-check"></i>
												</span>
											}
											uncheckedIcon={
												<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
													<i className="fas fa-times"></i>
												</span>
											}
										/>
									</div>
								</SettingsBaseItem>
							)}

							<div className="my-6 border-t border-white/10"></div>

							<SettingsBaseItem
								label={
									TIME_FINDER_SETTINGS.anonymous_voting.title
								}
								is_premium
								description={
									TIME_FINDER_SETTINGS.anonymous_voting
										.description
								}
								no_border
							>
								<div className="flex items-center">
									<Switch
										checked={
											props.does_have_premium &&
											anonymousVoting
										}
										onChange={(checked) => {
											if (props.does_have_premium) {
												setAnonymousVoting(checked);
											}
										}}
										disabled={
											!props.is_on_dashboard ||
											!props.does_have_premium
										}
										onColor="#00c3cc"
										onHandleColor="#00f4ff"
										checkedIcon={
											<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
												<i className="fas fa-check"></i>
											</span>
										}
										uncheckedIcon={
											<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
												<i className="fas fa-times"></i>
											</span>
										}
									/>
								</div>
							</SettingsBaseItem>

							<div className="my-6 border-t border-white/10"></div>

							<SettingsBaseItem
								label={TIME_FINDER_SETTINGS.hide_votes.title}
								is_premium
								description={
									TIME_FINDER_SETTINGS.hide_votes.description
								}
								no_border
							>
								<div className="flex items-center">
									<Switch
										checked={
											props.does_have_premium && hideVotes
										}
										onChange={(checked) => {
											if (props.does_have_premium) {
												setHideVotes(checked);
											}
										}}
										disabled={
											!props.is_on_dashboard ||
											!props.does_have_premium
										}
										onColor="#00c3cc"
										onHandleColor="#00f4ff"
										checkedIcon={
											<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
												<i className="fas fa-check"></i>
											</span>
										}
										uncheckedIcon={
											<span className="flex h-full flex-col items-center justify-center text-black opacity-50">
												<i className="fas fa-times"></i>
											</span>
										}
									/>
								</div>
							</SettingsBaseItem>

							<div className="my-6 border-t border-white/10"></div>

							<EligibleRolesSettingsItem
								selected_role_ids={eligibleRoleIds}
								onRolesChange={setEligibleRoleIds}
								disabled={!props.is_on_dashboard}
								is_enabled={isRestrictRolesEnabled}
								onToggle={setIsRestrictRolesEnabled}
								does_have_premium={props.does_have_premium}
								is_public={isPublic}
							/>
						</div>
					</InputSection>
				</div>

				<SeshPrimaryButton
					is_loading={props.is_loading}
					onClick={handleSubmit}
				>
					{props.is_edit
						? "Update Time Finder"
						: "Create Time Finder"}
				</SeshPrimaryButton>
			</div>
		</div>
	);
}
